// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// ── Clearing a status breaks what the status was claiming ────────────────────
//
// A line that says "Hard Bid" is making a claim about where its number came
// from, and the bid board reads that claim back: the partner whose price was
// accepted carries a "✓ Basis" chip against the line. Clearing the status in
// the estimate used to leave that chip standing, because the attribution
// (sourceBidId, or tradeName + tradeDate) lived on independently of the
// status that justified it.
//
// So the estimate said "no status" and the bid board said "this partner is the
// basis of the current budget", about the same line, at the same moment. Both
// were reading real data; neither was wrong on its own terms. That is the
// worst kind of inconsistency, because there is nothing to correct -- and an
// estimator who catches a tool contradicting itself once stops trusting the
// numbers they did not check.
//
// Clearing the status therefore clears the attribution with it. The price
// stays: the number is still the number, and deleting a figure somebody
// relies on because they changed a label would be its own betrayal. What goes
// is the claim about where it came from, which is exactly what was cleared.

/** The attribution fields a cost status justifies. */
export type StatusLinkPatch = {
  sourceBidId: undefined;
  tradeName: undefined;
  tradeDate: undefined;
};

/** Whether changing to this status should drop the line's bid attribution. */
export function clearsBidLink(next: string | null | undefined): boolean {
  return !next || String(next).trim() === "";
}

/**
 * The fields to unset alongside a cleared status.
 *
 * Returns null when the status still justifies an attribution, so a caller can
 * spread nothing in the common case.
 */
export function statusLinkPatch(next: string | null | undefined): StatusLinkPatch | null {
  if (!clearsBidLink(next)) return null;
  return { sourceBidId: undefined, tradeName: undefined, tradeDate: undefined };
}

/**
 * The status to display for a line, without inventing one.
 *
 * The bid board defaulted a missing status to "Hard Bid", so a line whose
 * status had been cleared was shown as a hard bid on the comparison screen --
 * the tool filling in an answer the estimator had deliberately removed.
 */
export function displayStatus(costStatus: string | null | undefined): string {
  const s = String(costStatus ?? "").trim();
  return s;
}
